import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { cn } from "@/lib/cn";
import { Icon, type IconName } from "@/components/icons/Icon";

interface ToastItem {
  id: string;
  msg: string;
  icon: IconName;
  duration: number;
}

interface ToastOptions {
  icon?: IconName;
  duration?: number;
}

type PushToast = (msg: string, opts?: ToastOptions) => void;

const ToastContext = createContext<PushToast | null>(null);

interface ToastProviderProps {
  children: ReactNode;
}

/** 토스트 알림 프로바이더 */
const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const push: PushToast = useCallback((msg, opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    const duration = opts.duration ?? 2400;
    const icon = opts.icon ?? "check";

    setToasts((prev) => [...prev, { id, msg, icon, duration }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-16 right-6 z-50 flex flex-col items-end gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              "flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-lg",
              "animate-in fade-in slide-in-from-bottom-2",
            )}
          >
            <Icon name={t.icon} size={14} className="text-emerald-400" />
            {t.msg}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/** 토스트 훅 — ToastProvider 내부에서 사용 */
const useToast = (): PushToast => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast 는 ToastProvider 안에서 사용해야 합니다.");
  return ctx;
};

export { ToastContext, ToastProvider, useToast };
export type { PushToast, ToastOptions };
